import { useEffect, useState, useRef } from "react";
import { Form, Formik } from "formik";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { toast } from "react-toastify";

import { useConfirm } from "../../hooks/confirm";
import dateFormatter from "../../util/dateFormatter";
import useNotificationService from "../../services/api/notificationService";
import { smsNotificationValidationSchema } from "../../schemas/smsNotification";
import { getNextDate } from "../../util/dateHelpers";
import { enums } from "../../constants/enum";

import {
  Badge,
  Button,
  IconButton,
  Loading,
  PageTitle,
  Section,
  Tab,
} from "../../components";
import {
  CustomerListTab,
  GroupListTab,
} from "../../components/NotificationTabs";
import { DatePicker, Radio, TextArea } from "../../components/forms";
import { CloseIcon, DeleteIcon, EditIcon } from "../../assets/icons";

import "./NotificationsPage.scss";

export default function ScheduledMessages() {
  const [editingMessage, setEditingMessage] = useState(null);

  if (editingMessage) {
    return (
      <EditScheduledMessage
        message={editingMessage}
        onBack={() => setEditingMessage(null)}
      />
    );
  }

  return <ScheduledMessagesList onEdit={(msg) => setEditingMessage(msg)} />;
}

function ScheduledMessagesList({ onEdit }) {
  const queryClient = useQueryClient();
  const { ref, inView } = useInView();
  const { getSmsHistory, deleteScheduleMessage, queryKey } =
    useNotificationService();
  const confirm = useConfirm();
  const deleteMutation = useMutation({
    mutationFn: deleteScheduleMessage,
    onSuccess: () => {
      toast.success("Message deleted successfully");
      queryClient.resetQueries({
        queryKey: [queryKey, enums.MESSAGE_TYPES.SCHEDULE],
      });
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
      );
    },
  });

  const { data, isLoading, error, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [queryKey, enums.MESSAGE_TYPES.SCHEDULE],
      queryFn: ({ pageParam = 1 }) =>
        getSmsHistory(enums.MESSAGE_TYPES.SCHEDULE, true, pageParam),
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (lastPage?.totalPages > lastPage?.currentPage) {
          return lastPage?.currentPage + 1;
        }
        return undefined;
      },
    });

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView]);

  if (isLoading) {
    return <Loading variant="fullScreen" />;
  }

  if (error) {
    toast.error(error.message || enums.DEFAULT_ERROR_MESSAGE);
  }

  const handleDelete = async (id) => {
    const isConfirmed = await confirm({
      title: "Delete Message?",
      description:
        "Are you sure you want to delete this message? This action cannot be undone.",
      confirmText: "Yes, Delete",
      cancelText: "No, Cancel",
    });

    if (isConfirmed) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="scheduled-messages">
      <h1 className="scheduled-messages-title">Scheduled Messages</h1>
      <div className="scheduled-messages-list">
        {data?.pages.map(({ messages }) =>
          messages.map((item) => (
            <Section
              key={item._id}
              wrapperClassName="scheduled-messages-content-wrapper"
              className="scheduled-messages-content"
            >
              <div className="scheduled-messages-content-type">
                <Badge text={`${item.type} MESSAGE`} />
              </div>
              <div className="scheduled-messages-content-meta">
                <div className="data">
                  <div className="content">
                    <Badge
                      text="Schedule Date & Time"
                      className="scheduled-messages-content-meta-badge"
                    />
                    <p>{dateFormatter(item.scheduleTime, true)}</p>
                  </div>
                  <div className="content">
                    <Badge
                      text="Receiver"
                      className="scheduled-messages-content-meta-badge"
                    />
                    <p>
                      {item.to?.length > 0
                        ? item.to?.length
                        : `${item.group?.name} group`}
                    </p>
                  </div>
                </div>
                <div className="action">
                  <Button
                    text="Edit"
                    icon={<EditIcon />}
                    className="scheduled-messages-content-action-edit"
                    onClick={() => onEdit(item._id)}
                  />
                  <IconButton
                    inverted
                    icon={<DeleteIcon />}
                    className="scheduled-messages-content-action-delete"
                    onClick={() => handleDelete(item._id)}
                  />
                </div>
              </div>
              <Section
                wrapperClassName="scheduled-messages-content-wrapper"
                className="scheduled-messages-content-message"
              >
                <div className="scheduled-messages-content-type">
                  <Badge text={`${item.type} MESSAGE`} />
                </div>
                <p>{item.message}</p>
              </Section>
            </Section>
          )),
        )}
      </div>

      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "20px 0",
        }}
        ref={ref}
      >
        {isFetchingNextPage ? <Loading variant="inline" /> : null}
      </div>
    </div>
  );
}

function EditScheduledMessage({ message, onBack }) {
  const queryClient = useQueryClient();
  const { updateScheduleMessage, getSmsById, queryKey } =
    useNotificationService();
  const { data: messageData, isLoading: messageLoading } = useQuery({
    queryKey: [queryKey, message, enums.MESSAGE_TYPES.SCHEDULE],
    queryFn: () => getSmsById(message),
    enabled: !!message,
  });
  const formikRef = useRef(null);

  const [activeContactTab, setActiveContactTab] = useState("Select Contacts");
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  useEffect(() => {
    if (messageData) {
      if (messageData.group) {
        setSelectedCustomers([
          {
            id: messageData.group._id,
            name: messageData.group.name,
          },
        ]);
      } else if (messageData.to && messageData.to.length > 0) {
        setSelectedCustomers(
          messageData.to.map((c) => ({
            id: c._id,
            name: c.name,
          })),
        );
      }
    }
  }, [messageData]);

  const initialValues = {
    to: messageData?.to?.map((c) => c._id) || [],
    group: messageData?.group?._id || messageData?.group || null,
    smsContentNature:
      messageData?.smsContentNature || enums.MESSAGE_TYPES.TRANSACTIONAL,
    message: messageData?.message || "",
    type: enums.MESSAGE_TYPES.SCHEDULE,
    scheduleDate: messageData?.scheduleTime
      ? new Date(messageData.scheduleTime).toISOString().split("T")[0]
      : "",
    scheduleTime: messageData?.scheduleTime
      ? new Date(messageData.scheduleTime).toTimeString().substring(0, 5)
      : "",
  };

  const { isPending, mutate } = useMutation({
    mutationFn: (payload) => updateScheduleMessage(message, { payload }),
    onSuccess: async (payload) => {
      await queryClient.resetQueries({
        queryKey: [queryKey, enums.MESSAGE_TYPES.SCHEDULE],
      });
      toast.success(payload?.message || "Message updated successfully");
      setSelectedCustomers([]);
      formikRef.current?.resetForm();
      onBack();
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.payload?.message || enums.DEFAULT_ERROR_MESSAGE,
      );
    },
  });

  const handleAddCustomer = (customer) => {
    // If it's a single customer (object with id)
    if (customer.id) {
      const customerId = customer.id;
      if (!selectedCustomers.find((c) => c.id === customerId)) {
        const newCustomers = [
          ...selectedCustomers,
          { ...customer, id: customerId },
        ];
        setSelectedCustomers(newCustomers);

        // Update Formik state directly
        const currentTo = formikRef.current?.values.to || [];
        formikRef.current?.setFieldValue("to", [...currentTo, customerId]);
      }
    }
  };

  const handleRemoveCustomer = (id) => {
    setSelectedCustomers(selectedCustomers.filter((c) => c.id !== id));

    // Update Formik state directly
    const currentTo = formikRef.current?.values.to || [];
    if (Object.values(enums.VIRTUAL_GROUPS).includes(id)) {
      formikRef.current?.setFieldValue("to", []);
    } else {
      formikRef.current?.setFieldValue(
        "to",
        currentTo.filter((cId) => cId !== id),
      );
    }
  };

  const handleAddGroup = ({
    selectedGroup = "",
    customerIds = [],
    groupId = null,
  }) => {
    // clear selected customers
    setSelectedCustomers([]);

    // set group as selected customer
    setSelectedCustomers((prev) => [
      ...prev,
      { name: selectedGroup, id: groupId ? groupId : selectedGroup },
    ]);
    // set group
    if (groupId) {
      formikRef.current?.setFieldValue("to", []);
      formikRef.current?.setFieldValue("group", groupId);
    } else {
      formikRef.current?.setFieldValue("group", null);
      formikRef.current?.setFieldValue("to", customerIds);
    }
  };

  const handleSubmit = (values) => {
    const payload = {
      to: values.to,
      group: values.group,
      message: values.message,
      smsContentNature: values.smsContentNature,
      type: values.type,
      scheduleTime: new Date(
        `${values.scheduleDate} ${values.scheduleTime}`,
      ).toISOString(),
    };
    mutate(payload);
  };

  if (messageLoading) {
    return <Loading variant="fullScreen" />;
  }

  return (
    <div className="notifications-edit-message">
      <PageTitle text="Edit Scheduled Message" prev={onBack} />

      <div className="notifications-content-grid">
        {/* Left Column: Contact Selection */}
        <div className="contact-selection-panel">
          <Tab
            tabs={["Select Contacts", "Groups"]}
            activeTab={activeContactTab}
            onTabChange={setActiveContactTab}
            className="contact-tabs"
          >
            {activeContactTab === "Select Contacts" && (
              <CustomerListTab onAddCustomer={handleAddCustomer} />
            )}
            {activeContactTab === "Groups" && (
              <GroupListTab
                onSelectGroup={handleAddGroup}
                disabled={selectedCustomers.length > 0}
              />
            )}
          </Tab>
        </div>
        {/* Right Column: Message Composition */}
        <div className="message-composition-panel">
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={smsNotificationValidationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values }) => (
              <Form>
                <div className="panel-section selected-customers">
                  <label>Selected Customers</label>
                  <div className="chips-container">
                    {selectedCustomers.length === 0 && (
                      <span className="placeholder-text">
                        No customers selected
                      </span>
                    )}
                    {selectedCustomers.map((c) => (
                      <Badge
                        key={c.id}
                        text={c.name}
                        icon={<CloseIcon className="remove-icon" />}
                        className="customer-chip"
                        onClick={() => handleRemoveCustomer(c.id)}
                      />
                    ))}
                  </div>
                </div>

                <div className="panel-section message-type">
                  <div className="radio-group">
                    <label className="radio-label">
                      <Radio
                        name="smsContentNature"
                        checked={
                          values.smsContentNature ===
                          enums.MESSAGE_TYPES.TRANSACTIONAL
                        }
                        value={enums.MESSAGE_TYPES.TRANSACTIONAL}
                      />
                      <span>Transactional Message</span>
                    </label>
                    <label className="radio-label">
                      <Radio
                        name="smsContentNature"
                        checked={
                          values.smsContentNature ===
                          enums.MESSAGE_TYPES.PROMOTIONAL
                        }
                        value={enums.MESSAGE_TYPES.PROMOTIONAL}
                      />
                      <span>Promotional Message</span>
                    </label>
                  </div>
                </div>

                <div className="panel-section message-body">
                  <TextArea
                    label="Message"
                    placeholder="Type here"
                    name="message"
                    maxLength={1500}
                  />
                </div>
                <div className="info-footer">
                  <p>Note:</p>
                  <ul>
                    <li>
                      A SMS page has a maximum character limit of 160
                      characters.
                    </li>
                    <li>
                      A SMS message can have a maximum size of 1500 characters.
                    </li>
                    <li>
                      The <strong>cost of a SMS page is 0.59 LKR.</strong>
                    </li>
                    <li>
                      The cost per SMS page is multiplied by the number of SMS
                      pages, which is the cost for one recipient. The total cost
                      is the product of the cost per recipient and the number of
                      recipients.
                    </li>
                  </ul>
                </div>
                <div className="schedule-date-time-selector">
                  <DatePicker
                    name="scheduleDate"
                    min={getNextDate(0).toISOString().split("T")[0]}
                  />
                  <DatePicker name="scheduleTime" type="time" />
                </div>
                <Button
                  text="Update Scheduled Message"
                  type="submit"
                  className="action-btn"
                  icon={<EditIcon />}
                  disabled={isPending}
                />
              </Form>
            )}
          </Formik>
        </div>

        <Loading variant="fullScreen" isLoading={isPending} />
      </div>
    </div>
  );
}
